import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Typography, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Input, 
  message,
  Popconfirm,
  Tooltip,
  Select
} from 'antd';
import { 
  BugOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { bugReportService } from '../../services/bugReportService';
import { BugReport, BugReportStatus } from '../../types';
import { formatLocalizedDateTime } from '../../utils/dateUtils';

const { Title } = Typography;
const { TextArea } = Input;

const BugReportList: React.FC = () => {
  const [bugReports, setBugReports] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingReport, setEditingReport] = useState<BugReport | null>(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [statusForm] = Form.useForm();
  const [selectedReport, setSelectedReport] = useState<BugReport | null>(null);
  const [form] = Form.useForm();
  const { user, isAdmin } = useAuth();
  const { t, i18n } = useTranslation();

  const fetchBugReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bugReportService.getMyBugReports();
      setBugReports(data);
    } catch (error: any) {
      message.error(t('bugReports.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBugReports();
  }, [fetchBugReports]);

  const handleCreate = () => {
    setEditingReport(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (report: BugReport) => {
    setEditingReport(report);
    form.setFieldsValue({
      subject: report.subject,
      description: report.description,
    });
    setModalVisible(true);
  };

  const handleSave = async (values: { subject: string; description: string }) => {
    try {
      if (editingReport) {
        // 수정 시에는 제목과 내용을 별도로 처리
        await bugReportService.updateBugReport(editingReport.report_id, {
          resolution_notes: `제목: ${values.subject}\n내용: ${values.description}`
        });
        message.success(t('bugReports.updateSuccess'));
      } else {
        await bugReportService.createBugReport(values);
        message.success(t('bugReports.createSuccess'));
      }
      setModalVisible(false);
      fetchBugReports();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('bugReports.saveFailed'));
    }
  };

  const handleDelete = async (reportId: string) => {
    try {
      await bugReportService.deleteBugReport(reportId);
      message.success(t('bugReports.deleteSuccess'));
      fetchBugReports();
    } catch (error: any) {
      message.error(t('bugReports.deleteFailed'));
    }
  };

  const handleStatusChange = (report: BugReport) => {
    setSelectedReport(report);
    statusForm.setFieldsValue({
      status: report.status,
      resolution_notes: report.resolution_notes || ''
    });
    setStatusModalVisible(true);
  };

  const handleStatusSave = async (values: { status: BugReportStatus; resolution_notes?: string }) => {
    if (!selectedReport) return;
    
    try {
      await bugReportService.updateBugReport(selectedReport.report_id, values);
      message.success(t('bugReports.statusChangeSuccess'));
      setStatusModalVisible(false);
      fetchBugReports();
    } catch (error: any) {
      message.error(t('bugReports.statusChangeFailed'));
    }
  };

  const getStatusColor = (status: BugReportStatus) => {
    switch (status) {
      case BugReportStatus.PENDING:
        return 'orange';
      case BugReportStatus.ON_HOLD:
        return 'blue';
      case BugReportStatus.RESOLVED:
        return 'green';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: BugReportStatus) => {
    switch (status) {
      case BugReportStatus.PENDING:
        return t('bugReports.status.pending');
      case BugReportStatus.ON_HOLD:
        return t('bugReports.status.onHold');
      case BugReportStatus.RESOLVED:
        return t('bugReports.status.resolved');
      default:
        return t('bugReports.status.unknown');
    }
  };

  const getStatusIcon = (status: BugReportStatus) => {
    switch (status) {
      case BugReportStatus.PENDING:
        return <ClockCircleOutlined />;
      case BugReportStatus.ON_HOLD:
        return <ClockCircleOutlined />;
      case BugReportStatus.RESOLVED:
        return <CheckCircleOutlined />;
      default:
        return null;
    }
  };

  const columns = [
    {
      title: t('bugReports.table.number'),
      dataIndex: 'report_id',
      key: 'report_id',
      width: 60,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => bugReports.length - index,
    },
    {
      title: t('bugReports.table.title'),
      dataIndex: 'subject',
      key: 'subject',
      width: 300,
      ellipsis: true,
      render: (subject: string) => (
        <div style={{ 
          maxWidth: '100%', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap' 
        }}>
          {subject}
        </div>
      ),
    },
    {
      title: t('bugReports.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center' as const,
      render: (status: BugReportStatus) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: t('bugReports.table.reporter'),
      dataIndex: 'user',
      key: 'user',
      width: 120,
      align: 'center' as const,
      render: (user: any) => user?.role === 'ADMIN' ? 'admin' : (user?.username || '-'),
    },
    {
      title: t('bugReports.table.reportedAt'),
      dataIndex: 'reported_at',
      key: 'reported_at',
      width: 140,
      align: 'center' as const,
      render: (date: string) => (
        <span style={{ fontSize: '12px' }}>
                          {formatLocalizedDateTime(date, i18n.language)}
        </span>
      ),
    },
    {
      title: t('bugReports.table.actions'),
      key: 'actions',
      width: 120,
      align: 'center' as const,
      render: (record: BugReport) => {
        const canEdit = isAdmin || record.user?.user_id === user?.user_id;
        const canDelete = isAdmin || record.user?.user_id === user?.user_id;
        
        return (
          <Space size="small">
            <Tooltip title={t('bugReports.table.view')}>
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => {
                  Modal.info({
                    title: record.subject,
                    width: 600,
                    content: (
                      <div>
                        <div style={{ marginBottom: 16 }}>
                          <strong>{t('bugReports.detail.status')}:</strong> {getStatusText(record.status)}
                        </div>
                        <div style={{ marginBottom: 16 }}>
                          <strong>{t('bugReports.detail.reporter')}:</strong> {record.user?.role === 'ADMIN' ? 'admin' : (record.user?.username || '-')}
                        </div>
                        <div style={{ marginBottom: 16 }}>
                          <strong>{t('bugReports.detail.reportedAt')}:</strong> {formatLocalizedDateTime(record.reported_at, i18n.language)}
                        </div>
                        {record.resolved_at && (
                          <div style={{ marginBottom: 16 }}>
                            <strong>{t('bugReports.detail.resolvedAt')}:</strong> {formatLocalizedDateTime(record.resolved_at, i18n.language)}
                          </div>
                        )}
                        <div style={{ marginBottom: 16 }}>
                          <strong>{t('bugReports.detail.content')}:</strong>
                          <div style={{ 
                            marginTop: 8, 
                            padding: 12, 
                            backgroundColor: '#f5f5f5', 
                            borderRadius: 4,
                            whiteSpace: 'pre-wrap'
                          }}>
                            {record.description}
                          </div>
                        </div>
                        {record.resolution_notes && (
                          <div>
                            <strong>{t('bugReports.detail.resolutionNotes')}:</strong>
                            <div style={{ 
                              marginTop: 8, 
                              padding: 12, 
                              backgroundColor: '#f0f9ff', 
                              borderRadius: 4,
                              whiteSpace: 'pre-wrap'
                            }}>
                              {record.resolution_notes}
                            </div>
                          </div>
                        )}
                      </div>
                    ),
                  });
                }}
              />
            </Tooltip>
            {canEdit && record.status === BugReportStatus.PENDING && (
              <Tooltip title={t('bugReports.table.edit')}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>
            )}
            {canDelete && (
              <Popconfirm
                title={t('bugReports.table.deleteConfirm')}
                onConfirm={() => handleDelete(record.report_id)}
                okText={t('bugReports.table.delete')}
                cancelText={t('common.cancel')}
              >
                <Tooltip title={t('bugReports.table.delete')}>
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            )}
                         {isAdmin && (
               <Tooltip title={t('bugReports.table.statusChange')}>
                 <Button
                   type="text"
                   size="small"
                   icon={<SettingOutlined />}
                   onClick={() => handleStatusChange(record)}
                   style={{ color: '#1890ff' }}
                 />
               </Tooltip>
             )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '16px' }}>
      <Card style={{ padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            {t('bugReports.title')}
          </Title>
          <Space>
            <span style={{ color: '#666' }}>
              {t('bugReports.totalReports', { count: bugReports.length })}
            </span>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              {t('bugReports.report')}
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={bugReports}
          rowKey="report_id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('pagination.showTotal', { 
              start: range[0], 
              end: range[1], 
              total: total 
            }),
          }}
          scroll={{ x: 800 }}
          size="small"
          style={{
            fontSize: '14px'
          }}
          locale={{
            emptyText: (
              <div style={{ padding: '40px 0', textAlign: 'center' }}>
                <BugOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <div style={{ color: '#666' }}>{t('bugReports.empty.title')}</div>
                <div style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                  {t('bugReports.empty.description')}
                </div>
              </div>
            )
          }}
        />
      </Card>

      {/* 오류 신고 작성/수정 모달 */}
      <Modal
        title={editingReport ? t('bugReports.modal.editTitle') : t('bugReports.modal.createTitle')}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setModalVisible(false);
          setEditingReport(null);
          form.resetFields();
        }}
        okText={editingReport ? t('bugReports.modal.edit') : t('bugReports.modal.register')}
        cancelText={t('bugReports.modal.cancel')}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            name="subject"
            label={t('bugReports.modal.subject')}
            rules={[{ required: true, message: t('bugReports.modal.requiredSubject') }]}
          >
            <Input placeholder={t('bugReports.modal.subjectPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('bugReports.modal.description')}
            rules={[{ required: true, message: t('bugReports.modal.requiredDescription') }]}
          >
            <TextArea
              rows={6}
              placeholder={t('bugReports.modal.descriptionPlaceholder')}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 상태 변경 모달 */}
      <Modal
        title={t('bugReports.modal.statusChangeTitle')}
        open={statusModalVisible}
        onOk={() => statusForm.submit()}
        onCancel={() => {
          setStatusModalVisible(false);
          setSelectedReport(null);
          statusForm.resetFields();
        }}
        okText={t('bugReports.modal.save')}
        cancelText={t('bugReports.modal.cancel')}
        width={500}
      >
        <Form
          form={statusForm}
          layout="vertical"
          onFinish={handleStatusSave}
        >
          <Form.Item
            name="status"
            label={t('bugReports.modal.status')}
            rules={[{ required: true, message: t('bugReports.modal.requiredStatus') }]}
          >
            <Select placeholder={t('bugReports.modal.statusPlaceholder')}>
              <Select.Option value={BugReportStatus.PENDING}>{t('bugReports.status.pending')}</Select.Option>
              <Select.Option value={BugReportStatus.ON_HOLD}>{t('bugReports.status.onHold')}</Select.Option>
              <Select.Option value={BugReportStatus.RESOLVED}>{t('bugReports.status.resolved')}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="resolution_notes"
            label={t('bugReports.modal.resolutionNotes')}
          >
            <TextArea
              rows={4}
              placeholder={t('bugReports.modal.resolutionNotesPlaceholder')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BugReportList; 